import React, { useEffect, useState, useRef } from 'react';
import { Button } from '@wordpress/components';
import { RichText, useBlockProps } from '@wordpress/block-editor';
import apiFetch from '@wordpress/api-fetch';
import Select from 'react-select';
import Swal from 'sweetalert2';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
const TriggerNotificationsBlock = ({ attributes, setAttributes, isEditor }) => {
    const { selectedUser, selectedCourse, selectedTime, notificationMessage, selectedTrigger } = attributes || {};
    const [users, setUsers] = useState([]);
    const [courses, setCourses] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [dealerships, setDealerships] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Local state for the form fields on the frontend
    const [user, setUser] = useState(selectedUser || {label: 'Select Users', value: 0});
    const [course, setCourse] = useState(selectedCourse || {label: 'Select Courses', value: 0});
    const [time, setTime] = useState(selectedTime || { hours: 0, minutes: 0 });
    const [content, setContent] = useState(notificationMessage || null);
    const [contentMessage, setContentMessage] = useState(notificationMessage || null);
    const [trigger, setTrigger] = useState(selectedTrigger || { label: 'Single User', value: 'single_user' });
    const [userRole, setUserRole] = useState({label: 'Select Role', value: ''});
    const [dealership, setDealership] = useState({label: 'Select Dealership', value: ''});

    // State for the selected tab
    const [selectedTab, setSelectedTab] = useState('recurring');
    const [selectedDate, setSelectedDate] = useState(null);
    const today = new Date();
    // Calculate tomorrow's date in YYYY-MM-DD format
    const getTomorrowDate = () => {
        const date = new Date();
        date.setDate(date.getDate() + 1);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
    };


    useEffect(() => {
        // Event handler for when filters are applied
        const handleCourseChangeEvent = (event) => {
            const { selected_courses } = event.detail;
            if (selected_courses) {
                const selected = courses.find(course => course.id === selected_courses);
                if (selected) {
                    const option = {label: selected.name, value: selected.id};
                    setCourse(option);
                    if (setAttributes) {
                        setAttributes({ selectedCourse: option });
                    }
                } else {
                    setCourse({label: 'Select Courses', value: 0});
                    if (setAttributes) {
                        setAttributes({ selectedCourse: {label: 'Select Courses', value: 0} });
                    }
                }
            } else {
                    const selected = courses.find(course => course.id === 0);
                setCourse({label: 'Select Courses', value: 0});
                if (setAttributes) {
                    setAttributes({ selectedCourse: {label: 'Select Courses', value: 0} });
                }
            }
        };

        // Add the event listener for the filters applied.
        document.addEventListener("wisdm-ld-reports-filters-applied", handleCourseChangeEvent);

        // Cleanup the event listener when the component unmounts
        return () => {
            document.removeEventListener("wisdm-ld-reports-filters-applied", handleCourseChangeEvent);
        };
    }, [courses]); 


    const hasMounted = useRef(false); // Ref to track if the component has mounted

    useEffect(() => {
        // Only set selectedDate to tomorrow on initial mount
        if (!hasMounted.current) {
            hasMounted.current = true;
            if (!selectedDate) {
                setSelectedDate(getTomorrowDate()); // Only set it once on mount
            }

            const fetchData = async () => {
                try {
                    setIsLoading(true);

                    // Fetch users, courses, roles, and dealerships
                    const usersData = await apiFetch({ path: 'rp/v1/wdm-get-users', method: 'GET' });
                    const rolesData = await apiFetch({ path: 'rp/v1/wdm-get-roles', method: 'GET' });
                    const dealershipsData = await apiFetch({ path: 'rp/v1/wdm-get-dealerships', method: 'GET' });
                    const coursesData = await apiFetch({ path: 'rp/v1/wdm-get-courses', method: 'GET' });
                    
                    // Fetch notification content (email subject and content)
                    const notificationData = await apiFetch({ path: 'rp/v1/get-notification-message', method: 'GET' });
                    // Set the notification content into state
                    if (notificationData && notificationData.email_content) {
                        setContent(notificationData.email_content); // Set the email content to state
                        setContentMessage(notificationData.email_content); // Set the email content to state
                    }

                    // Format the data with a default "Select" option
                    setUsers([{ id: 0, name: 'Select Users' }, ...usersData]);
                    setUserRoles([{ id: '', name: 'Select Role' }, ...rolesData]);
                    setDealerships([{ id: '', name: 'Select Dealership' }, ...dealershipsData]);
                    setCourses([{ id: 0, name: 'Select Courses' }, ...coursesData]);
                } catch (error) {
                    console.error('Error fetching data:', error);
                } finally {
                    setIsLoading(false);
                }
            };

            if (!isEditor) {
                fetchData(); // Call fetchData only once when component mounts and isEditor is false
            } else {
                setUsers([{ id: 0, name: 'Select Users' }]);
                setCourses([{ id: 0, name: 'Select Courses' }]);
                setUserRoles([{ id: '', name: 'Select Role' }]);
                setDealerships([{ id: '', name: 'Select Dealership' }]);
                setIsLoading(false);
            }

            const checkElementExistence = () => {
                const targetElement = document.querySelector('.wisdm-learndash-reports-report-filters [id^="tab:r"][id$=":1"]');
                const notificationWrapper = document.querySelector('.wdm-trigger-notifications-block-wrapper');
            
                if (targetElement && notificationWrapper) {
                    // Clear the interval once the element is found
                    clearInterval(interval);
            
                    // Set up MutationObserver to detect changes in the class of targetElement
                    const observer = new MutationObserver(() => {
                        if (targetElement.classList.contains('react-tabs__tab--selected')) {
                            notificationWrapper.style.display = 'none';
                        } else {
                            notificationWrapper.style.display = 'block';
                        }
                    });
            
                    // Start observing the target element for class changes
                    observer.observe(targetElement, {
                        attributes: true, // Observe changes to attributes (including class)
                        attributeFilter: ['class'], // Only observe class changes
                    });
                }
            };
            
            // Poll every 500ms to check if the element is rendered
            const interval = setInterval(checkElementExistence, 500);
        }
    }, []);

    

    // Handle user selection change
    const handleUserChange = (value) => {
        setUser(value);
        if (setAttributes) {
            setAttributes({ selectedUser: value });
        }
    };

    // Handle user role selection change
    const handleUserRoleChange = (value) => {
        setUserRole(value);
        if (setAttributes) {
            setAttributes({ selectedUserRole: value });
        }
    };

    // Handle dealership selection change
    const handleDealershipChange = (value) => {
        setDealership(value);
        if (setAttributes) {
            setAttributes({ selectedDealership: value });
        }
    };

    // Handle course selection change
    const handleCourseChange = (value) => {
        setCourse(value);
        if (setAttributes) {
            setAttributes({ selectedCourse: value });
        }
    };

    // Handle trigger selection change
    const handleTriggerChange = (value) => {
        setTrigger(value);
        if (setAttributes) {
            setAttributes({ selectedTrigger: value });
        }
    };

    // Handle date selection change
    const handleDateChange = (date) => {
        const newDate = new Date(date);
        // Convert to ISO string and slice the date part
        const formattedDate = newDate.toISOString().split('T')[0];
        setSelectedDate(formattedDate);
    };

    // Handle time input change
    const handleTimeChange = (newTime) => {
        const [hours, minutes] = newTime.split(':').map(Number);
        if (!isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
            setTime({ hours, minutes });
            if (setAttributes) {
                setAttributes({ selectedTime: { hours, minutes } });
            }
        }
    };

    // Handle rich text change
    const handleRichTextChange = (value) => {
        setContent(value);
        if (setAttributes) {
            setAttributes({ notificationMessage: value });
        }
    };

    // Format hours and minutes to always have two digits
    const formattedTime = `${String(time.hours).padStart(2, '0')}:${String(time.minutes).padStart(2, '0')}`;

    // Handle form submit
    const handleSubmit = async () => {
        // Validation based on the selected trigger
        if (
            (trigger.value === 'single_user' && (!user || user.value === 0)) ||
            (trigger.value === 'user_role' && (!userRole || userRole.value === '')) ||
            (trigger.value === 'dealership' && (!dealership || dealership.value === '')) ||
            (selectedTab !== 'single' && course.value === 0) ||
            (selectedTab === 'single' && !selectedDate)
        ) {
            console.error('Please select a valid user, role, dealership, or course (and date) before submitting.');
            Swal.fire({
                title: 'Error!',
                text: 'Please select a valid user, role, dealership, or course (and date) before submitting.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
            return;
        }
    
        // Determine the trigger type to pass in the submission
        const triggerData = {
            trigger: trigger.value, // Trigger type: single_user, user_role, or dealership
            userId: trigger.value === 'single_user' ? user?.value ?? null : null,
            userRoleId: trigger.value === 'user_role' ? userRole?.value ?? null : null,
            dealershipId: trigger.value === 'dealership' ? dealership?.value ?? null : null,
        };
        // Handle course and date selections
        const scheduledTime = formattedTime; // Time formatted (HH:MM)
        setIsSubmitting(true);

        try {
            const response = await apiFetch({
                path: '/rp/v1/wdm-trigger-notifications',
                method: 'POST',
                data: {
                    ...triggerData,  // Spread the trigger-related data
                    courseId: selectedTab === 'recurring' && course ? course.value : null,
                    date: selectedTab === 'single' ? selectedDate : null,
                    time: scheduledTime,
                    message: content,
                },
            });
            setCourse({label: 'Select Courses', value: 0});
            setDealership({label: 'Select Dealership', value: ''});
            setUserRole({label: 'Select Role', value: ''});
            setUser({label: 'Select Users', value: 0});
            setSelectedDate(getTomorrowDate());
            const newTime = "00:00";
            const [hours, minutes] = newTime.split(':').map(Number);
            setTime({ hours, minutes });
            setContent(contentMessage);
            // Show SweetAlert popup with the response message
            Swal.fire({
                title: 'Success!',
                text: response?.message || 'Notification added successfully.',
                icon: 'success',
                confirmButtonText: 'OK',
            });
        } catch (error) {
            console.error('Error submitting notification:', error);
            Swal.fire({
                title: 'Error!',
                text: error?.message || 'An error occurred while adding the notification.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        } finally {
            setIsSubmitting(false);
        }
    };
    

    const blockProps = isEditor ? useBlockProps() : {};
    return (
        <div {...blockProps}>
            <div className="wdm-trigger-notifications-block">
                 {/* Tab buttons */}
                 <div className="tab-buttons">
                    <button 
                        className={selectedTab === 'recurring' ? 'active' : ''}
                        onClick={() => setSelectedTab('recurring')}
                    >
                        Recurring
                    </button>
                    <button 
                        className={selectedTab === 'single' ? 'active' : ''}
                        onClick={() => setSelectedTab('single')}
                    >
                        Single
                    </button>
                </div>

                <span className="chart-title">Course Completion Notification</span>

                {/* Trigger Selection */}
                <div className="select-container">
                        <span className="selector-label">Set Trigger</span>
                        <Select
                            value={trigger}
                            options={[
                                { label: 'Single User', value: 'single_user' },
                                { label: 'User Role', value: 'user_role' },
                                { label: 'Dealership', value: 'dealership' },
                            ]}
                            onChange={handleTriggerChange}
                            placeholder="Choose a trigger"
                        />
                    </div>

                {/* Conditionally show the Select dropdowns based on the trigger */}
                {trigger.value === 'single_user' && (
                <div className="select-container">
                    {/* Label for the Select dropdown */}
                    <span className="selector-label">Select User</span>
                    
                    <Select
                        value={user}
                        options={isLoading
                            ? [{label: 'Select Users', value: 0},{ label: 'Loading users...', value: '' }]
                            : users.map(user => ({ label: user.name, value: user.id }))}
                        onChange={handleUserChange}
                        placeholder="Choose a user"
                    />
                </div>
            )}

                {/* User Role Dropdown (if trigger is 'user_role') */}
                {trigger.value === 'user_role' && (
                    <div className="select-container">
                        <span className="selector-label">Select User Role</span>
                        <Select
                            value={userRole}
                            options={isLoading 
                                ? [{label: 'Select Role', value: ''},{ label: 'Loading roles...', value: '' }] 
                                : userRoles.map(role => ({ label: role.name, value: role.id }))}
                            onChange={handleUserRoleChange}
                            placeholder="Choose a role"
                        />
                    </div>
                )}

                {/* Dealership Dropdown (if trigger is 'dealership') */}
                {trigger.value === 'dealership' && (
                    <div className="select-container">
                        <span className="selector-label">Select Dealership</span>
                        <Select
                            value={dealership}
                            options={isLoading 
                                ? [{label: 'Select Dealership', value: ''},{ label: 'Loading dealerships...', value: '' }] 
                                : dealerships.map(dealer => ({ label: dealer.name, value: dealer.id }))}
                            onChange={handleDealershipChange}
                            placeholder="Choose a dealership"
                        />
                    </div>
                )}

                {/* Conditional fields based on the selected tab */}
                {selectedTab === 'single' && (
                    <>
                        <div className="date-picker">
                            <div className="date-label">
                                <label className="selector-label">Select Date</label>
                            </div>
                            <div className="date-input">
                                <DatePicker
                                    selected={selectedDate}
                                    onChange={handleDateChange}
                                    dateFormat="MM/dd/yyyy" // Month, Day, Year format
                                    minDate={today}
                                    required
                                />
                            </div>
                        </div>
                    </>
                )}

                {/* Course Dropdown (if selectedTab is 'recurring') */}
                {selectedTab === 'recurring' && (
                    <div className="select-container">
                        <span className="selector-label">Select Course</span>
                        <Select
                            value={course}
                            options={isLoading 
                                ? [{label: 'Select Courses', value: 0},{ label: 'Loading courses...', value: '' }] 
                                : courses.map(course => ({ label: course.name, value: course.id }))}
                            onChange={handleCourseChange}
                            placeholder="Choose a course"
                        />
                    </div>
                )}

                {/* Time picker */}
                <div className="time-picker">
                    <label className="selector-label">Time (HH:MM)</label>
                    <input
                        id="time-input"
                        type="time"
                        value={formattedTime}
                        onChange={(e) => handleTimeChange(e.target.value)}
                    />
                </div>

                {/* Notification Message Editor */}
                <div className="notification-message-editor">
                    <label className="selector-label">Notification Message</label>
                    <RichText
                        tagName="div"
                        value={content}
                        onChange={handleRichTextChange}
                        placeholder="Type the notification message here..."
                    />
                </div>

                {/* Submit Button */}
                <Button
                    isPrimary
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Scheduling...' : 'Schedule'}
                </Button>
            </div>
        </div>
    );
};

export default TriggerNotificationsBlock;
